<script setup lang="ts">
import { Play } from 'lucide-vue-next'
import { Link } from '@inertiajs/vue3'

interface Video {
  id: number
  slug: string
  title: string
  thumbnail?: string
  duration?: number
  tutor: {
    name: string
  }
  view_count: number
  created_at: string
}

interface Props {
  videos: Video[]
  basePath?: string
}

const props = withDefaults(defineProps<Props>(), {
  basePath: '/library',
})

const formatDuration = (seconds?: number) => {
  if (!seconds) return '00:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const formatViews = (count: number) => {
  if (!count) return '0'
  if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K'
  }
  return count.toString()
}

const formatTimeAgo = (date: string) => {
  const now = new Date()
  const then = new Date(date)
  const days = Math.floor((now.getTime() - then.getTime()) / (1000 * 60 * 60 * 24))

  if (days === 0) return 'today'
  if (days === 1) return '1 day ago'
  if (days < 7) return `${days} days ago`
  if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`
  return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''} ago`
}
</script>

<template>
  <section id="related-videos" class="bg-white rounded-xl border border-gray-200 p-5">
    <h3 class="text-lg font-bold text-gray-900 mb-4">Related Videos</h3>
    <div class="space-y-4">
      <Link
        v-for="video in videos"
        :key="video.id"
        :href="`${basePath}/${video.slug}`"
        class="flex gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
      >
        <div class="relative w-36 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex-shrink-0 overflow-hidden">
          <img
            v-if="video.thumbnail"
            :src="video.thumbnail"
            :alt="video.title"
            class="w-full h-full object-cover"
          />
          <div v-else class="absolute inset-0 flex items-center justify-center">
            <Play class="w-8 h-8 text-white opacity-80" />
          </div>
          <div class="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1.5 py-0.5 rounded">
            {{ formatDuration(video.duration) }}
          </div>
        </div>
        <div class="flex-1 min-w-0">
          <h4 class="font-semibold text-gray-900 text-sm mb-1 line-clamp-2">{{ video.title }}</h4>
          <p class="text-xs text-gray-600 mb-1">{{ video.tutor.name }}</p>
          <p class="text-xs text-gray-500">
            {{ formatViews(video.view_count) }} views • {{ formatTimeAgo(video.created_at) }}
          </p>
        </div>
      </Link>

      <!-- Empty State -->
      <div v-if="videos.length === 0" class="text-center py-8">
        <p class="text-gray-500 text-sm">No related videos found</p>
      </div>
    </div>
  </section>
</template>
